import { Box, Button, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import { updateVideoSettingsEndpoint, updateVideoTranscriptEndpoint } from "../../../../api/endpoints";
import { IconAttachment } from "../../../../utils/icons";
import { TranscriptPanel } from "./TranscriptPanel";
import {
    Cue,
    ParsedVtt,
    extractTimestamps,
    formatShortTimestamp,
    parseSrt,
    parseVtt,
    serializeSrt,
    serializeVtt,
    timestampToSeconds,
    transcriptFromCues
} from "./transcriptEditing";
import { uploadTranscriptFile } from "./uploadTranscriptFile";
import { DotLottleLoader } from "../../../../components/Loader";
import { PanelFooter } from "./PanelFooter";

interface Props {
    video: any;
    srtContent: string | null;
    vttContent: string | null;
    srtKey: string | null;
    vttKey: string | null;
    dbTranscript: string | null;
    transcriptStatus: string;
    loading: boolean;
    onTranscriptRequested: () => void;
    onSaved: (updated: { srt: string; vtt: string; transcript: string }) => void;
    currentTime?: number;
    onSeek?: (seconds: number) => void;
}

const applyCueTextToVtt = (parsed: ParsedVtt, srtCues: Cue[]): ParsedVtt => {
    const cueBlockCount = parsed.blocks.filter((b) => b.type === "cue").length;
    if (cueBlockCount !== srtCues.length) {
        throw new Error("SRT and VTT cue counts do not match — re-import the file before editing.");
    }
    let i = 0;
    return {
        ...parsed,
        blocks: parsed.blocks.map((b) => {
            if (b.type !== "cue") return b;
            const updated = { ...b, cue: { ...b.cue, text: srtCues[i].text } };
            i++;
            return updated;
        })
    };
};

export function TranscriptCueEditor({
    video,
    srtContent,
    vttContent,
    srtKey,
    vttKey,
    dbTranscript,
    transcriptStatus,
    loading,
    onTranscriptRequested,
    onSaved,
    currentTime,
    onSeek
}: Props) {
    const [cues, setCues] = useState<Cue[]>([]);
    const [originalCues, setOriginalCues] = useState<Cue[]>([]);
    const [saving, setSaving] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        if (!srtContent) {
            setCues([]);
            setOriginalCues([]);
            return;
        }
        try {
            const parsed = parseSrt(srtContent);
            setCues(parsed);
            setOriginalCues(parsed);
        } catch (err: any) {
            toast.error(err?.message || "Failed to parse transcript file.");
            setCues([]);
            setOriginalCues([]);
        }
    }, [srtContent]);

    const isDirty = useMemo(
        () => cues.length !== originalCues.length || cues.some((c, i) => c.text !== originalCues[i]?.text),
        [cues, originalCues]
    );

    const activeCueIndex = useMemo(() => {
        if (typeof currentTime !== "number" || cues.length === 0) return -1;
        return cues.findIndex((cue) => {
            const ts = extractTimestamps(cue.timing);
            if (!ts) return false;
            const start = timestampToSeconds(ts.start);
            const end = timestampToSeconds(ts.end);
            if (Number.isNaN(start) || Number.isNaN(end)) return false;
            return currentTime >= start && currentTime < end;
        });
    }, [currentTime, cues]);

    const updateCueText = (idx: number, text: string) => {
        setCues((prev) => prev.map((c, i) => (i === idx ? { ...c, text } : c)));
    };

    const onCopy = () => {
        const text = transcriptFromCues(cues);
        if (!text) return;
        navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const onSave = async () => {
        if (!srtContent || !vttContent) return;
        setSaving(true);
        try {
            const vttParsed = parseVtt(vttContent);
            const updatedVtt = applyCueTextToVtt(vttParsed, cues);
            const newSrt = serializeSrt(cues);
            const newVtt = serializeVtt(updatedVtt);
            const newTranscript = transcriptFromCues(cues);

            if (!srtKey || !vttKey || !video.id) {
                throw new Error("Cannot determine S3 location for transcript files.");
            }

            await Promise.all([
                uploadTranscriptFile({
                    fileId: video.id,
                    key: srtKey,
                    content: newSrt,
                    format: "srt"
                }),
                uploadTranscriptFile({
                    fileId: video.id,
                    key: vttKey,
                    content: newVtt,
                    format: "vtt"
                })
            ]);

            await axiosInstant.patch(updateVideoTranscriptEndpoint(video.id), {
                transcript: newTranscript
            });

            toast.success("Transcript saved.");
            setOriginalCues(cues);
            onSaved({ srt: newSrt, vtt: newVtt, transcript: newTranscript });
        } catch (err: any) {
            console.error("Failed to save transcript:", err);
            toast.error(err?.response?.data?.message || err?.message || "Failed to save transcript.");
        } finally {
            setSaving(false);
        }
    };

    const isOnProject = !!(video.projectId ?? video.project?.id);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full py-12">
                <DotLottleLoader fill={false} sx={{ width: 56, height: 56 }} />
            </div>
        );
    }

    // Missing DB transcript means import didn't fully complete, even when SRT/VTT
    // are present in S3. Show the Request Transcript UI so a re-import can fix it.
    if (!dbTranscript) {
        return (
            <TranscriptPanel
                transcript={null}
                videoId={video.id}
                hasTranscript={false}
                transcriptStatus={transcriptStatus}
                isOnProject={isOnProject}
                onTranscriptRequested={onTranscriptRequested}
            />
        );
    }

    // DB transcript exists but no SRT/VTT — read-only view (per-cue edit needs timestamps).
    if (!srtContent || !vttContent) {
        return (
            <TranscriptPanel
                transcript={dbTranscript}
                videoId={video.id}
                hasTranscript={true}
                transcriptStatus={transcriptStatus}
                isOnProject={isOnProject}
                onTranscriptRequested={onTranscriptRequested}
            />
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <CueTimeline
                cues={cues}
                activeIndex={activeCueIndex}
                saving={saving}
                onSeek={onSeek}
                updateCueText={updateCueText}
                isPlaying={
                    typeof currentTime === "number" &&
                    currentTime > 0 &&
                    activeCueIndex >= 0
                }
            />

            <PanelFooter>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onSave}
                    disabled={!isDirty || saving}
                    startIcon={saving ? <DotLottleLoader fill={false} sx={{ width: 20, height: 20 }} /> : undefined}
                    sx={{ textTransform: "capitalize" }}
                >
                    {saving ? "Saving..." : "Save Transcript"}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onCopy}
                    disabled={saving || cues.length === 0}
                    endIcon={<IconAttachment stroke="#fff" />}
                    sx={{ textTransform: "capitalize" }}
                >
                    {isCopied ? "Copied!" : "Copy Transcript"}
                </Button>
            </PanelFooter>
        </Box>
    );
}

interface TimelineProps {
    cues: Cue[];
    activeIndex: number;
    saving: boolean;
    isPlaying: boolean;
    onSeek?: (seconds: number) => void;
    updateCueText: (idx: number, text: string) => void;
}

function CueTimeline({
    cues,
    activeIndex,
    saving,
    isPlaying,
    onSeek,
    updateCueText,
}: TimelineProps) {
    const activeRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        activeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [activeIndex]);

    return (
        <Box
            sx={{
                flex: 1,
                overflowY: "auto",
                maxHeight: 420,
                pt: 3,
                pb: 3,
                "@keyframes cuePulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.3 },
                },
            }}
        >
            <Box sx={{ position: "relative", pl: 3.5 }}>
                <Box
                    sx={{
                        position: "absolute",
                        left: 12,
                        top: 0,
                        bottom: 0,
                        width: "2px",
                        bgcolor: "var(--border)",
                        borderRadius: 1,
                    }}
                />
                {cues.map((cue, i) => {
                    const ts = extractTimestamps(cue.timing);
                    const label = ts
                        ? `${formatShortTimestamp(ts.start)} → ${formatShortTimestamp(ts.end)}`
                        : "—";
                    const startSeconds = ts ? timestampToSeconds(ts.start) : NaN;
                    const canSeek = !!onSeek && !Number.isNaN(startSeconds);
                    const isActive = i === activeIndex;

                    return (
                        <Box
                            key={i}
                            ref={isActive ? activeRef : null}
                            sx={{
                                position: "relative",
                                mb: "6px",
                                "&:last-child": { mb: 0 },
                                cursor: canSeek ? "pointer" : "default",
                            }}
                            onClick={() => canSeek && onSeek?.(startSeconds)}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: -20,
                                    top: 12,
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    border: "2px solid",
                                    borderColor: isActive ? "var(--accent)" : "#D5D0C8",
                                    bgcolor: isActive ? "var(--accent)" : "var(--surface-2)",
                                    boxShadow: isActive
                                        ? "0 0 0 3px rgba(171,133,74,0.18)"
                                        : "none",
                                    transition: "all 0.2s",
                                    zIndex: 1,
                                }}
                            />
                            <Box
                                sx={{
                                    px: "12px",
                                    py: "8px",
                                    borderRadius: "10px",
                                    border: "1px solid",
                                    borderColor: isActive ? "var(--accent)" : "var(--border)",
                                    bgcolor: isActive ? "var(--tint)" : "var(--surface)",
                                    transition: "all 0.15s",
                                    "&:hover": {
                                        borderColor: isActive ? "var(--accent)" : "#D5D0C8",
                                        boxShadow: isActive
                                            ? "none"
                                            : "var(--sh-1)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        mb: "2px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily:
                                                "ui-monospace, SFMono-Regular, Menlo, monospace",
                                            fontSize: 11,
                                            fontWeight: 500,
                                            letterSpacing: 0.2,
                                            color: isActive ? "var(--accent-text)" : "var(--muted)",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    {isActive && isPlaying && (
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                bgcolor: "var(--accent)",
                                                animation: "cuePulse 1.2s ease-in-out infinite",
                                            }}
                                        />
                                    )}
                                </Box>
                                <TextField
                                    value={cue.text}
                                    onChange={(e) => updateCueText(i, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    multiline
                                    minRows={1}
                                    variant="standard"
                                    fullWidth
                                    margin="none"
                                    disabled={saving}
                                    InputProps={{
                                        disableUnderline: true,
                                        sx: {
                                            fontFamily: "inherit",
                                            fontSize: 14,
                                            lineHeight: 1.4,
                                            color: "var(--text)",
                                            fontWeight: isActive ? 500 : 400,
                                            p: 0,
                                            m: 0,
                                            "& textarea, & input": {
                                                p: 0,
                                                m: 0,
                                            },
                                            "&:focus-within": {
                                                borderBottom: "1px dashed var(--accent)",
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
